import { useState } from 'react';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { NSCard } from 'bricks';
import tdcBackground from 'assets/images/tdc-background.webp';
import {
    CardKey,
    IUsesSummary,
    IProFormaGeneralInfo,
    IProFormaUses,
    SaveInput,
} from 'views/ProFormaTable/types';
import {
    HIGH_LEVEL_DESCRIPTIONS,
    isCardEditable,
    getEditableUseLine,
    getUsesCards,
    buildSavePatch,
} from './helpers';
import UsesCard from './UsesCard/UsesCard';
import UsesCardEditView from './UsesCardEditView/UsesCardEditView';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { isLandDevelopmentModel } from 'helpers/proForma';
import './UsesCardsOverview.scss';

interface Props {
    uses: IUsesSummary;
    proForma: IProFormaGeneralInfo;
    hasWriteAccessToProForma: boolean;
}

const UsesCardsOverview = ({ uses, proForma, hasWriteAccessToProForma }: Props) => {
    const { handleUpdateBudgetClassifications } = useProFormaSocketContext();
    const [editingCard, setEditingCard] = useState<CardKey | null>(null);

    const cards = getUsesCards(uses, proForma.proFormaModelId).filter(card => {
        if (isLandDevelopmentModel(proForma.proFormaModelId) && card.key === 'operatingReserve') return false;
        return true;
    });

    const handleSave = (highLevel: IProFormaUses | undefined) => (input: SaveInput) => {
        const useLine = getEditableUseLine(highLevel);
        if (!useLine) return;
        handleUpdateBudgetClassifications([{ id: useLine.id, ...buildSavePatch(input) }]);
    };

    return (
        <div className="UsesCardsOverview d-flex flex-column">
            <div className="UsesCardsOverview__row scrollable-container d-flex mb-3">
                {cards.map(({
                    key, highLevel, supplemental, editVariant, editContext,
                    editableTotal, editableCostPerUnit, highLevelCostPerUnit,
                    unitLabel, showCostPerUnitInput, initialEditMode, calculatedSublines,
                }, index) => {
                    const id = `UsesCard-${key}`;
                    const title = highLevel?.description ?? HIGH_LEVEL_DESCRIPTIONS[key];
                    const isLastCard = index === cards.length - 1;

                    return editingCard === key ? (
                        <UsesCardEditView
                            key={key}
                            id={id}
                            title={title}
                            total={editableTotal}
                            costPerUnit={editableCostPerUnit}
                            showCostPerUnitInput={showCostPerUnitInput}
                            variant={editVariant}
                            context={editContext}
                            initialMode={initialEditMode}
                            calculatedSublines={calculatedSublines}
                            isLastCard={isLastCard}
                            onSave={handleSave(highLevel)}
                            onCancel={() => setEditingCard(null)}
                        />
                    ) : (
                        <UsesCard
                            key={key}
                            id={id}
                            title={title}
                            total={highLevel?.amount}
                            costPerUnit={highLevelCostPerUnit}
                            unitLabel={unitLabel}
                            supplemental={supplemental}
                            isLocked={!hasWriteAccessToProForma || !isCardEditable(highLevel)}
                            isLastCard={isLastCard}
                            onEnterEdit={() => setEditingCard(key)}
                        />
                    );
                })}
            </div>
            <NSCard className="NSCard--level-5 position-relative overflow-hidden rounded-lg p-3">
                <img className="UsesCardsOverview__total-bg position-absolute w-100 h-100" src={tdcBackground} alt="" />
                <div className="position-relative d-flex flex-column">
                    <div className="text-dark mb-2">Total development cost</div>
                    <div className="text-white font-24 font-weight-semibold">
                        {uses.totals?.amount != null ? numeralFormatterCurrency(uses.totals.amount, '$0,0') : '—'}
                    </div>
                </div>
            </NSCard>
        </div>
    );
};

export default UsesCardsOverview;
